import React, { memo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Input, Menu, Skeleton, Spin } from 'antd';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import {
  ApprovalWorkflowControllerCountApprovalWorkflowsParams,
  ApprovalWorkflowItemDto
} from '@api/Api';
import ApprovalEmptyStateWrapper from '@components/Approval/ApprovalEmptyStateWrapper/ApprovalEmptyStateWrapper';
import PageSortPanel from '@components/PageSortPanel';
import LottieComponent from '@components/Lottie';
import { ReactComponent as SearchSvg } from '@assets/icons/search.svg';
import StatusesFilter from '@components/Approval/StatusesFilter/StatusesFilter';
import DatesFilter from '@components/Approval/DatesFilter/DatesFilter';
import ApprovalsTable from '@components/Approval/ApprovalsTable/ApprovalsTable';

import './ApprovalsTab.less';

type ApprovalsTabProps = {
  approvals: ApprovalWorkflowItemDto[];
  approvalWorkflowsQueryParams: Omit<
    ApprovalWorkflowControllerCountApprovalWorkflowsParams,
    'workspaceId'
  >;
  campaignId: string;
  hasNext: boolean;
  loading: boolean;
  isFetchingNextPage: boolean;
  loadMoreApprovals: () => void;
  refetchApprovalsData: () => void;
};

export default memo(function ApprovalsTab({
  approvals,
  loadMoreApprovals,
  refetchApprovalsData,
  campaignId,
  hasNext,
  loading,
  isFetchingNextPage,
  approvalWorkflowsQueryParams
}: ApprovalsTabProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParam = searchParams.get('search');
  const sortByParam = searchParams.get('sortBy') || 'needReview';
  const timeoutId = useRef<ReturnType<typeof setTimeout>>();
  const hasActiveFilter = Object.values(approvalWorkflowsQueryParams).some(
    (i) => !!i
  );

  return (
    <div className="approvals-tab">
      <div className="approvals-tab_head">
        <div className="approvals-tab_head-filter">
          <DatesFilter
            onChange={(dates) => {
              searchParams.delete('dueDate');
              if (dates?.length) {
                dates.forEach((date) => searchParams.append('dueDate', date));
              }
              setSearchParams(searchParams);
            }}
            date={
              approvalWorkflowsQueryParams.dueDateFrom &&
              approvalWorkflowsQueryParams.dueDateTo
                ? [
                    approvalWorkflowsQueryParams.dueDateFrom,
                    approvalWorkflowsQueryParams.dueDateTo
                  ]
                : []
            }
            timeOptions={['today', 'tomorrow', 'nextWeek', 'nextMonth']}
          />
          <StatusesFilter
            onChange={(statuses) => {
              searchParams.delete('statuses');
              if (statuses.length) {
                statuses.forEach((status) =>
                  searchParams.append('statuses', status)
                );
              }
              setSearchParams(searchParams);
            }}
            selectedStatuses={approvalWorkflowsQueryParams.statuses || []}
          />
          <DatesFilter
            onChange={(dates) => {
              searchParams.delete('createdDate');
              if (dates?.length) {
                dates.forEach((date) =>
                  searchParams.append('createdDate', date)
                );
              }
              setSearchParams(searchParams);
            }}
            date={
              approvalWorkflowsQueryParams.createdAtFrom &&
              approvalWorkflowsQueryParams.createdAtTo
                ? [
                    approvalWorkflowsQueryParams.createdAtFrom,
                    approvalWorkflowsQueryParams.createdAtTo
                  ]
                : []
            }
            placeholder="Created date"
            timeOptions={['today', 'tomorrow', 'prevWeek', 'prevMonth']}
          />
        </div>
        <div className="approvals-tab_head-sort">
          <PageSortPanel
            isVisibleSortStyle={false}
            entity="approvals"
            sortMenuModal={
              <Menu
                selectedKeys={[sortByParam]}
                style={{
                  minWidth: 260
                }}
                onClick={({ key }) => {
                  searchParams.set('sortBy', key);
                  setSearchParams(searchParams);
                }}
                items={[
                  { label: 'Need to review', key: 'needReview' },
                  { label: 'Already completed review', key: 'completedReview' }
                ]}
              />
            }
            searchInputComponent={
              <Input
                style={{ marginRight: 16, padding: '8px 12px' }}
                placeholder="Search"
                prefix={<SearchSvg />}
                size="large"
                defaultValue={searchParam || ''}
                onChange={(e) => {
                  const _searchText = e.target.value.trim();
                  if (timeoutId.current) clearTimeout(timeoutId.current);
                  timeoutId.current = setTimeout(() => {
                    if (_searchText) searchParams.set('search', _searchText);
                    if (!_searchText) searchParams.delete('search');
                    setSearchParams(searchParams);
                  }, 400);
                }}
              />
            }
          />
        </div>
      </div>
      <div
        style={{
          height: 'calc(100vh - 352px - var(--demo-mode-panel-width, 0px))',
          overflow: 'auto'
        }}
      >
        <InfiniteScroll
          loading={isFetchingNextPage ? 'more' : false}
          next={loadMoreApprovals}
          hasMore={hasNext ?? false}
          loader={
            <>
              <Skeleton.Input />
              <Skeleton.Input />
            </>
          }
        >
          {loading && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%'
              }}
            >
              <Spin size="large" />
            </div>
          )}
          {!approvals.length && hasActiveFilter && !loading && (
            <div className="empty_state empty_state--search">
              <LottieComponent className="icon" view="noSearchingResult" />
              <strong
                className="main-body-text main-body-text--bold"
                style={{ marginBottom: 8 }}
              >
                No results found
              </strong>
              <p className="copy main-body-text main-body-text--dark-gray">
                It seems we can’t find any results based on your search
              </p>
            </div>
          )}
          {!approvals.length && !hasActiveFilter && !loading && (
            <ApprovalEmptyStateWrapper
              listLength={approvals.length}
              refetchApprovalsData={refetchApprovalsData}
              campaignId={campaignId}
              wholePage={true}
            />
          )}
          {!!approvals.length && !loading && (
            <ApprovalsTable
              refetchApprovalsData={refetchApprovalsData}
              approvals={approvals}
            />
          )}
        </InfiniteScroll>
      </div>
    </div>
  );
});
